// components/booking/BookingStepSchedule.js
import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Clock, Ban } from 'lucide-react';
import BookingSummary from './BookingSummary';
import AdminService from '../../services/AdminService';

const BookingStepSchedule = ({
  serviceDetails,
  schedulingDetails,
  setSchedulingDetails,
  onNextStep,
  onBack,
  onChangeService
}) => {
  const [blockedDates, setBlockedDates] = useState({});

  // Load blocked dates on mount
  useEffect(() => {
    AdminService.getBlockedDates().then(setBlockedDates).catch(() => {});
  }, []);

  // Helper: Date → "YYYY-MM-DD"
  const toDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const isDayBlocked = (date) => blockedDates[toDateKey(date)]?.allDay === true;

  const getBlockedTimes = (date) => {
    const entry = blockedDates[toDateKey(date)];
    if (!entry) return [];
    if (entry.allDay) return timeSlots;
    return entry.times || [];
  };

  // Available time slots - simplified array
  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
  ];

  // Get next 7 available dates
  const getDates = () => {
    const dates = [];
    const today = new Date();

    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  // Helper function to get service duration based on service ID
  const getServiceDuration = () => {
    if (!serviceDetails) return 'Please select a service';
    
    // Map service IDs to durations
    const durations = {
      'silver': '1-2 hours',
      'gold': '2-3 hours',
      'diamond': '4-6 hours',
      'classic_exterior': '1-2 hours',
      'wax_polish': '2-3 hours',
      'cut_polish': '4-6 hours',
      'classic_interior': '1-2 hours',
      'comfort_interior': '2-3 hours',
      'deep_clean': '3-4 hours'
    };
    
    return durations[serviceDetails.id] || '2-3 hours';
  };
  
  return (
    <div className="bg-white rounded-xl shadow-md p-6 mb-6">
      {/* Service summary banner */}
      <BookingSummary 
        serviceDetails={serviceDetails}
        onChangeService={onChangeService}
      />
      
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Date Selection */}
        <div>
          <h4 className="font-medium text-gray-700 mb-4">Select Date</h4>
          <div className="grid grid-cols-3 gap-3">
            {getDates().map((date, index) => {
              const blocked = isDayBlocked(date);
              return (
                <button
                  key={index}
                  disabled={blocked}
                  className={`border p-3 rounded-lg text-center transition-all ${
                    blocked
                      ? 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed'
                      : schedulingDetails.date &&
                        schedulingDetails.date.toDateString() === date.toDateString()
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => {
                    if (!blocked) setSchedulingDetails({...schedulingDetails, date, time: null});
                  }}
                >
                  {blocked && <Ban className="h-3 w-3 mx-auto mb-0.5 text-red-400" />}
                  <div className="text-xs text-gray-500">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                  <div className="text-lg font-medium">{date.getDate()}</div>
                  <div className="text-xs text-gray-500">
                    {blocked ? 'Unavailable' : date.toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Time Selection */}
        <div>
          <h4 className="font-medium text-gray-700 mb-4">Select Time</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {timeSlots.map((time, index) => {
              const blocked = schedulingDetails.date
                ? getBlockedTimes(schedulingDetails.date).includes(time)
                : false;
              return (
                <button
                  key={index}
                  disabled={blocked}
                  className={`border p-3 rounded-lg text-center transition-all ${
                    blocked
                      ? 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed line-through'
                      : schedulingDetails.time === time
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => { if (!blocked) setSchedulingDetails({...schedulingDetails, time}); }}
                >
                  <div className="font-medium">{time}</div>
                </button>
              );
            })}
          </div>
          
          {/* Service Duration Info */}
          <div className="mt-6 bg-blue-50 p-4 rounded-lg">
            <div className="flex items-start">
              <Clock className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-800">Estimated Service Time</p>
                <p className="text-gray-600">Approximately {getServiceDuration()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button 
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 flex items-center"
          onClick={onBack}
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back
        </button>
        
        <button 
          className={`px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-lg font-medium flex items-center ${
            !schedulingDetails.date || !schedulingDetails.time ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'
          }`}
          onClick={onNextStep}
          disabled={!schedulingDetails.date || !schedulingDetails.time}
        >
          Continue
          <ArrowRight className="ml-2 h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default BookingStepSchedule;